import type { PresenteeismFailureAttemptsServiceModel } from "@services/PresenteeismFailureAttemptsService";

export interface PresenteeismFailureListPresentationItem {
    id: string;
    userName: string;
    requesterID: string;
    buildingName: string;
    failureReason: string;
    failureReasonLabel: string;
    errorMessage: string;
    errorMessageLabel: string;
    type: string;
    timestamp: number;
    distanceInMeters?: number;
    attempt: PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt;
}

const secondFactorFailureReasons = new Set([
    "MOBILE_PERMISSION_PROBLEM",
    "MOCK_LOCATION_DETECTED",
    "LOCATION_MISMATCH",
    "STALE_LOCATION",
    "LOW_ACCURACY",
    "SECOND_FACTOR_SESSION_ALREADY_CONSUMED",
    "SECOND_FACTOR_CHALLENGE_ALREADY_CONSUMED",
    "SECOND_FACTOR_FORWARDED_SESSION_REJECTED",
    "SECOND_FACTOR_FORWARDED_CHALLENGE_REJECTED",
]);

export const getPresenteeismFailureReasonLabel = (reason: string) => {
    return reason.localized();
};

export const getPresenteeismFailureMessageLabel = (message: string) => {
    return message.localized();
};

export const isSecondFactorFailureReason = (reason: string) => {
    return reason.startsWith("SECOND_FACTOR_") || secondFactorFailureReasons.has(reason);
};

export const isSecondFactorFailureAttempt = (
    attempt: PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt,
) => {
    return attempt.metadata?.system === "second-factor-authentication"
        || isSecondFactorFailureReason(attempt.failureReason);
};

const hasDisplayableValue = (value: unknown) => {
    if (value === undefined || value === null || value === "") {
        return false;
    }

    if (typeof value === "object") {
        return Object.keys(value).length > 0;
    }

    return true;
};

export const hasSecondFactorFailureMetadata = (
    metadata?: PresenteeismFailureAttemptsServiceModel.FailureAttemptMetadata,
) => {
    if (!metadata) {
        return false;
    }

    return [
        metadata.sessionID,
        metadata.challengeID,
        metadata.problemType,
        metadata.problemDetails,
        metadata.appMetadata,
        metadata.rawPermissionState,
        metadata.organizationName,
        metadata.offenderPhoneNumber,
        metadata.deviceInstallationID,
        metadata.detectedDevicePlatform,
        metadata.detectedDeviceNextAction,
        metadata.detectedDeviceUserAgent,
        metadata.boundDevicePlatform,
        metadata.requestUserAgent,
        metadata.source,
        metadata.completionMethodAttempted,
        metadata.analytics?.controlName,
        metadata.analytics?.controlOutcome,
        metadata.analytics?.controlReason,
        metadata.analytics?.attemptedCompletionMethod,
        metadata.analytics?.detectedDevicePlatform,
        metadata.analytics?.detectedDeviceNextAction,
        metadata.analytics?.boundDevicePlatform,
        metadata.analytics?.requestPlatform,
    ].some(hasDisplayableValue);
};

export const mapPresenteeismFailureAttemptPresentation = (
    row: PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt,
): PresenteeismFailureListPresentationItem => ({
    id: row.firestoreRecordId ?? `${row.ticketID}-${row.timestamp}`,
    userName: row.user?.name ?? row.requesterID,
    requesterID: row.requesterID,
    buildingName: row.building?.name ?? row.closestQontactPoint?.buildingName ?? "",
    failureReason: row.failureReason,
    failureReasonLabel: getPresenteeismFailureReasonLabel(row.failureReason),
    errorMessage: row.errorMessage,
    errorMessageLabel: getPresenteeismFailureMessageLabel(row.errorMessage),
    type: row.type,
    timestamp: row.timestamp,
    distanceInMeters: row.closestQontactPoint?.distanceInMeters,
    attempt: row,
});
